// ChatHeader.js
import React, { useCallback, useEffect, useState, useRef } from "react";

import {
  Text,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  HStack,
  useToast,
  useBreakpointValue,
  Tooltip,
  Spinner,
  Box,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  useClipboard,
  Button,
} from "@chakra-ui/react";
import { ReactComponent as DownloadIcon } from "../../assets/icons/download_2_fill_icon.svg";
import qrCode from "../../assets/images/qr-code.png";
import officialLogo from "../../assets/images/official-logo.png";
import { useMemo } from "react";
import Chart from "chart.js/auto";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { PDFDocument, rgb, StandardFonts } from "pdf-lib";
import { useTranslation } from "../LanguageTranslation/TranslationContext";
import { BsThreeDotsVertical } from "react-icons/bs";
import { FaShareAlt, FaUserPlus } from "react-icons/fa";
import { FaClipboardList } from "react-icons/fa";
import Translate from "../../components/LanguageTranslation/Translate";
import { useCurrency } from "../CurrencyExchange/CurrencyContext";
import SettingsMenu from "../common/SettingsMenu";
import InviteManagerModal from "../common/InviteManagerModal";
import TravelGroupCircles from "../common/TravelGroupCircles";

const currencyToSymbol = {
  EUR: "€",
  USD: "$",
  GBP: "£",
  CZK: "Kč",
  DKK: "kr",
  NOK: "kr",
  SEK: "kr",
  JPY: "¥",
  CNY: "¥",
  KRW: "₩",
  IDR: "Rp",
  MYR: "RM",
  PHP: "₱",
  VND: "₫",
  THB: "฿",
};

/* ---------- helpers ---------- */
const hexToRgb = (hex) => {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  return rgb(r, g, b);
};

/** Build an off-screen Chart.js doughnut and return a PNG data URL */
const getDoughnutDataURL = (distribution) =>
  new Promise((resolve) => {
    const canvas = document.createElement("canvas");
    canvas.width = 240;
    canvas.height = 240;

    const labels = distribution.map((d) => d.label);
    const values = distribution.map((d) => d.value);
    const colors = distribution.map((d) => d.hex);

    new Chart(canvas.getContext("2d"), {
      type: "doughnut",
      data: {
        labels,
        datasets: [{ data: values, backgroundColor: colors, borderWidth: 0 }],
      },
      plugins: [ChartDataLabels],
      options: {
        responsive: false,
        cutout: "60%",
        plugins: {
          legend: { display: false },
          datalabels: {
            color: "#000",
            formatter: (_, ctx) => {
              const v = values[ctx.dataIndex];
              const pct = (
                (v / values.reduce((a, c) => a + c, 0)) *
                100
              ).toFixed(0);
              return `${pct}%`;
            },
            font: { size: 9 },
          },
        },
      },
      plugins: [
        {
          id: "resolver",
          afterRender: (c) => {
            resolve(canvas.toDataURL("image/png"));
            c.destroy();
          },
        },
      ],
    });
  });

const BudgetHeader = ({
  tripName,
  expenses,
  totalBudget,
  onShareOpen,
  selectedTrip,
  isBase,
}) => {
  const { translateText } = useTranslation();
  const { currency, convertCurrency } = useCurrency();
  const toast = useToast();
  const [downloading, setDownloading] = useState(false);
  const isXL = useBreakpointValue({ base: false, xl: true });
  const inviteModal = useDisclosure();
  // console.log("expenses", expenses);

  const drawLeaderLabels = (pg, cx, cy, rOuter, data, font, fontSize = 9) => {
    const total = data.reduce((s, d) => s + d.value, 0);
    let startAng = -Math.PI / 2; // Chart.js starts at 12 o'clock

    const docCurrencySymbol = currencyToSymbol[currency];
    data.forEach((slice) => {
      const sweep = (slice.value / total) * 2 * Math.PI;
      const midAng = startAng + sweep / 2;
      const cos = Math.cos(midAng);
      const sin = Math.sin(midAng);

      const lineStartX = cx + cos * (rOuter + 2);
      const lineStartY = cy - sin * (rOuter + 2);
      const lineEndX = cx + cos * (rOuter + 25);
      const lineEndY = cy - sin * (rOuter + 25);

      pg.drawLine({
        start: { x: lineStartX, y: lineStartY },
        end: { x: lineEndX, y: lineEndY },
        thickness: 0.8,
        color: slice.color,
      });

      const txt = `${slice.label}: ${docCurrencySymbol} ${Number(
        convertCurrency(slice.value, "USD")
      ).toFixed(2)} (${currency})`;
      const alignLeft = cos < 0;
      const pad = 3;
      const txtWidth = font.widthOfTextAtSize(txt, fontSize);
      const textX = alignLeft ? lineEndX - txtWidth - pad : lineEndX + pad;
      const textY = lineEndY - fontSize / 2;

      pg.drawText(txt, { x: textX, y: textY, size: fontSize, font });

      startAng += sweep;
    });
  };
  /* 1. flatten expenses */
  const flatExpenses = useMemo(() => {
    if (!Array.isArray(expenses)) return [];
    return expenses.flatMap((cat) =>
      (cat.expenses || []).map((e) => ({
        description: e.expense_name,
        category: cat.category_name,
        date: new Date(e.expense_date).toISOString().split("T")[0],
        location: e.expense_location?.label || "",
        total: e.true_cost ?? e.cost ?? 0,
        catColor: cat.color,
      }))
    );
  }, [expenses]);

  /* 2. aggregate + colour map */
  const budgetData = useMemo(() => {
    const totalsByCat = {};
    flatExpenses.forEach((r) => {
      totalsByCat[r.category] = (totalsByCat[r.category] || 0) + r.total;
    });

    const distribution = Object.entries(totalsByCat).map(([label, value]) => {
      const hex =
        expenses.find((c) => c.category_name === label)?.color || "#000";
      return { label, value, hex, color: hexToRgb(hex) };
    });

    const spent = flatExpenses.reduce((s, r) => s + r.total, 0);
    return { allocated: spent, spent, remaining: 0, distribution };
  }, [flatExpenses, expenses]);

  /* 3. PDF generator */
  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      toast({
        title: "Downloading PDF...",
        description: "Your travel budget summary is being prepared.",
        status: "info",
        duration: 3000,
        isClosable: true,
      });
      /* 3-a. get doughnut PNG */
      const chartURL = await getDoughnutDataURL(budgetData.distribution);
      const chartBytes = await fetch(chartURL).then((r) => r.arrayBuffer());

      /* 3-b. create pdf-lib doc */
      const pdfDoc = await PDFDocument.create();
      let page = pdfDoc.addPage([600, 800]);
      const { width, height } = page.getSize();

      /* constants */
      const MARGIN = 50;
      const USABLE = width - MARGIN * 2;
      const LEFT_X = MARGIN;
      const RIGHT_X = MARGIN + USABLE / 2 + 10;
      const DIVIDER_Y = height - 110;
      const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
      const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
      const green = rgb(0.2196, 0.4627, 0.1137);
      const ltGreen = rgb(0.95, 0.98, 0.95);
      const greyLine = rgb(0.75, 0.75, 0.75);

      const logoBytes = await fetch(officialLogo).then((res) =>
        res.arrayBuffer()
      );
      const logoImage = await pdfDoc.embedPng(logoBytes);
      const qrImage = await pdfDoc.embedPng(
        await fetch(qrCode).then((r) => r.arrayBuffer())
      );
      const chartImg = await pdfDoc.embedPng(chartBytes);
      const docCurrencySymbol = currencyToSymbol[currency];
      /* ───── translations ───── */
      const translatedTripName = await translateText(tripName);
      const travelBudgetText = await translateText("Travel Budget ");
      const summaryText = await translateText("Summary");
      const budgetBoxTitle = await translateText("Budget and Expenses");
      // const allocatedText = await translateText("Allocated:");
      const allocatedText =
        (await translateText("Allocated:")) +
        ` ${docCurrencySymbol} ${Number(
          convertCurrency(totalBudget, "USD")
        ).toFixed(2)} (${currency})`;

      const spentText =
        (await translateText("Spent:")) +
        ` ${docCurrencySymbol} ${Number(
          convertCurrency(budgetData.spent, "USD")
        ).toFixed(2)} (${currency})`;

      const remainingText =
        (await translateText("Remaining:")) +
        ` ${docCurrencySymbol} ${Number(
          convertCurrency(budgetData.remaining, "USD")
        ).toFixed(2)} (${currency})`;

      const expenseDetailsText = await translateText("Expense Details");
      const totalExpensesText = await translateText("Total expenses:");
      const taglineText = await translateText("Stay Wild, Explore More");

      /* table headers */
      const hdrs = await Promise.all(
        ["Description", "Type", "Quantity", "Cost per Unit", "Total Cost"].map(
          (t) => translateText(t)
        )
      );
      const colW = [120, 90, 60, 100, 100];
      const colCount = hdrs.length;

      /* helpers */
      const wrap = (txt, fnt, fs, max) => {
        const words = String(txt).split(" ");
        const lines = [];
        let cur = "";
        for (const w of words) {
          const test = cur ? `${cur} ${w}` : w;
          if (fnt.widthOfTextAtSize(test, fs) <= max) cur = test;
          else {
            if (cur) lines.push(cur);
            cur = w;
          }
        }
        if (cur) lines.push(cur);
        return lines;
      };

      // --- Table header drawing helper ---
      const drawTableHeader = (pg, y) => {
        pg.drawRectangle({
          x: LEFT_X,
          y,
          width: innerW,
          height: headerH,
          color: green,
        });
        let colX = LEFT_X;
        hdrs.forEach((h, i) => {
          pg.drawText(h, {
            x: colX + rowPadX,
            y: y + headerH / 2 - 4,
            size: 9,
            font: boldFont,
            color: rgb(1, 1, 1),
          });
          colX += colW[i];
        });
        pg.drawRectangle({
          x: LEFT_X,
          y,
          width: innerW,
          height: 0.5,
          color: greyLine,
        });
      };

      // --- Truncate Description to one line with ellipsis ---
      const truncateToWidth = (txt, fnt, fs, max) => {
        let truncated = txt;
        while (
          fnt.widthOfTextAtSize(truncated, fs) > max &&
          truncated.length > 0
        ) {
          truncated = truncated.slice(0, -1);
        }
        if (truncated.length < txt.length) {
          // Remove a few more chars to fit the ellipsis
          while (
            fnt.widthOfTextAtSize(truncated + "...", fs) > max &&
            truncated.length > 0
          ) {
            truncated = truncated.slice(0, -1);
          }
          return truncated + "...";
        }
        return truncated;
      };

      /* ───── header ───── */
      const drawHeader = (pg) => {
        pg.drawImage(logoImage, {
          x: LEFT_X,
          y: height - 50,
          width: 140,
          height: 25,
        });

        pg.drawText(travelBudgetText, {
          x: LEFT_X,
          y: height - 90,
          size: 24,
          font: boldFont,
          color: green,
        });
        const titleW = font.widthOfTextAtSize("Travel Budget ", 24);
        pg.drawText(summaryText, {
          x: LEFT_X + titleW + 15,
          y: height - 90,
          size: 24,
          font,
          color: green,
        });
        wrap(translatedTripName, boldFont, 12, USABLE / 2 - 10).forEach(
          (ln, i) =>
            pg.drawText(ln, {
              x: RIGHT_X,
              y: height - 35 - i * 14,
              size: 12,
              font: boldFont,
            })
        );
        pg.drawRectangle({
          x: LEFT_X,
          y: DIVIDER_Y,
          width: USABLE,
          height: 0.5,
          color: greyLine,
        });
      };

      // --- Footer split: base and QR ---
      const drawFooterBase = (pg) => {
        pg.drawRectangle({ x: 0, y: 0, width, height: 55, color: green });
        const center = (txt, y0, fs = 11, f = boldFont) =>
          pg.drawText(txt, {
            x: width / 2 - font.widthOfTextAtSize(txt, fs) / 2,
            y: y0,
            size: fs,
            font: f,
            color: rgb(1, 1, 1),
          });
        center(taglineText, 37, 16);
        center("www.nomad360.io", 20, 11, font); // domain stays literal
        center("© 2025 NOMAD360. All Rights Reserved.", 5, 7, font); // legal string left literal
      };
      const drawFooterQR = (pg) => {
        pg.drawImage(qrImage, {
          x: width - 140 - MARGIN,
          y: 60,
          width: 140,
          height: 140,
        });
      };

      // --- Use new header/footer on all pages ---
      drawHeader(page);
      drawFooterBase(page);

      /* ---- choose chart size once ---- */
      const CHART_W = 100;
      const CHART_H = 100;

      /* ---------------- budget box ---------------- */
      const boxH = 170;
      const boxY = DIVIDER_Y - boxH - 10;
      page.drawRectangle({
        x: LEFT_X,
        y: boxY,
        width: USABLE,
        height: boxH,
        color: ltGreen,
        borderColor: green,
        borderWidth: 1,
      });
      page.drawText(budgetBoxTitle, {
        x: LEFT_X + 10,
        y: boxY + boxH - 20,
        size: 13,
        font: boldFont,
        color: green,
      });

      const textY = boxY + boxH - 38;
      page.drawText(allocatedText, {
        x: LEFT_X + 10,
        y: textY,
        size: 11,
        font,
      });
      page.drawText(spentText, {
        x: LEFT_X + 10,
        y: textY - 18,
        size: 11,
        font,
      });
      page.drawText(remainingText, {
        x: LEFT_X + 10,
        y: textY - 36,
        size: 11,
        font,
      });

      /* ------------- embed chart ------------- */
      const CHART_X = RIGHT_X + 10;
      const CHART_Y = boxY + (boxH - CHART_H) / 2;

      page.drawImage(chartImg, {
        x: CHART_X,
        y: CHART_Y,
        width: CHART_W,
        height: CHART_H,
      });

      /* ------------- leader labels ------------- */
      const translatedDistribution = await Promise.all(
        budgetData.distribution.map(async (slice) => ({
          ...slice,
          label: await translateText(slice.label),
        }))
      );

      const chartCX = CHART_X + CHART_W / 2;
      const chartCY = CHART_Y + CHART_H / 2;

      drawLeaderLabels(
        page,
        chartCX,
        chartCY,
        CHART_W / 2,
        translatedDistribution,
        font
      );

      /* ───────── Expense Details table ───────── */
      // Only declare these variables once
      const innerW = colW.reduce((a, c) => a + c, 0);
      const headerH = 22; // header row height
      const rowPadY = 3; // vertical padding within cells
      const rowPadX = 4; // horizontal padding
      const totH = 18; // <-- move this here!

      /* 1️⃣  Title + margin below */
      let y = boxY - 30;
      page.drawText(expenseDetailsText, {
        x: LEFT_X,
        y,
        size: 13,
        font: boldFont,
        color: green,
      });
      y -= 30;

      /* 2️⃣  Header row (green) */
      drawTableHeader(page, y);
      y -= headerH;

      // Only declare drawGridLine once
      const drawGridLine = (x0, yy, hh) =>
        page.drawRectangle({
          x: x0,
          y: yy,
          width: 0.5,
          height: hh,
          color: greyLine,
        });

      /* 3️⃣  Data rows (all-white, with pagination) */
      for (const row of flatExpenses) {
        // translate the text fields
        const desc = await translateText(row.description);
        const cat = await translateText(row.category);

        const cells = [
          // Description column: truncate to one line with ellipsis
          truncateToWidth(desc, font, 9, colW[0] - rowPadX * 2),
          cat,
          String(row.quantity ?? ""),
          row.costPerUnit
            ? `${docCurrencySymbol} ${Number(
                convertCurrency(row.costPerUnit, "USD")
              ).toFixed(2)} (${currency})`
            : "",
          `${docCurrencySymbol} ${Number(
            convertCurrency(row.total, "USD")
          ).toFixed(2)} (${currency})`,
        ];

        // Only Description column is single line, others can wrap
        const wrapped = cells.map((val, i) => {
          if (i === 0) {
            return [val]; // already truncated, single line
          }
          return wrap(String(val), font, 9, colW[i] - rowPadX * 2);
        });
        const lineCnt = Math.max(...wrapped.map((w) => w.length));
        const rowH = lineCnt * 9 + rowPadY * 2;

        // --- Pagination logic ---
        // If not enough space for row + totals + footer, add new page
        // Reserve at least (totH + 60) for totals/footer (adjust as needed)
        if (y - rowH < 60 + totH) {
          page = pdfDoc.addPage([600, 800]);
          drawHeader(page);
          drawFooterBase(page);
          const CONTENT_START_Y = height - 140; // adjust this value as needed
          y = CONTENT_START_Y;
          page.drawText(expenseDetailsText, {
            x: LEFT_X,
            y,
            size: 13,
            font: boldFont,
            color: green,
          });
          y -= 30;
          drawTableHeader(page, y);
          y -= headerH;
        }

        /* background */
        page.drawRectangle({
          x: LEFT_X,
          y,
          width: innerW,
          height: rowH,
          color: rgb(1, 1, 1),
        });

        /* draw cell text & vertical grid lines */
        let xPtr = LEFT_X;
        wrapped.forEach((wLines, i) => {
          wLines.forEach((ln, idx) => {
            const txtY = y + rowH - rowPadY - 9 * (idx + 1);
            page.drawText(ln, { x: xPtr + rowPadX, y: txtY, size: 9, font });
          });
          xPtr += colW[i];
          if (i < colCount - 1) drawGridLine(xPtr, y, rowH);
        });

        /* bottom horizontal rule */
        page.drawRectangle({
          x: LEFT_X,
          y,
          width: innerW,
          height: 0.5,
          color: greyLine,
        });

        y -= rowH;
      }

      /* 4️⃣  Totals row (green) */
      page.drawRectangle({
        x: LEFT_X,
        y: y - totH,
        width: innerW,
        height: totH,
        color: green,
      });
      page.drawText(totalExpensesText, {
        x: LEFT_X + rowPadX,
        y: y - totH + 5,
        size: 10,
        font: boldFont,
        color: rgb(1, 1, 1),
      });
      page.drawText(
        `${docCurrencySymbol} ${Number(
          convertCurrency(budgetData.spent, "USD")
        ).toFixed(2)} (${currency})`,
        {
          x: LEFT_X + innerW - colW[colCount - 1] + rowPadX,
          y: y - totH + 5,
          size: 10,
          font: boldFont,
          color: rgb(1, 1, 1),
        }
      );
      // Draw both base footer and QR code only on the last page
      drawFooterBase(page);
      drawFooterQR(page);

      /*  ---- save ----  */
      const pdfBytes = await pdfDoc.save();
      const blob = new Blob([pdfBytes], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const link = Object.assign(document.createElement("a"), {
        href: url,
        download: `${translatedTripName}_Travel_Budget_Summary.pdf`,
      });
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <HStack
      display={{ base: "flex", md: "flex" }}
      p={2}
      pl={4}
      justifyContent="space-between"
      alignItems="center"
      mb={4}
      borderBottom="1px"
      borderColor="blackAlpha.100"
      height="8vh"
    >
      <Text
        fontSize={{ base: "lg", md: "sm" }}
        fontWeight="bold"
        color="blackAlpha.800"
        mb={0}
      >
        <Translate text="Budget Planner" />
      </Text>

      <HStack spacing={0} justifyContent="flex-end">
        {isBase && selectedTrip && (
          <TravelGroupCircles
            tripId={selectedTrip}
            availableResource="budget_planner"
          />
        )}
        {isXL  ? (
          <>
          { selectedTrip && ( 
            <>
            <Tooltip label="Download">
              <IconButton
                mr={2}
                size="sm"
                icon={
                  downloading ? (
                    <Spinner size="sm" color="#286328" />
                  ) : (
                    <Box as={DownloadIcon} boxSize="5" />
                  )
                }
                aria-label="Download"
                bg="none"
                _hover={{ bg: "none" }}
                onClick={handleDownloadPDF}
                isDisabled={downloading}
              />
            </Tooltip>
            <Tooltip label="Invite User">
              <IconButton
                size="sm"
                icon={<Box as={FaUserPlus} boxSize="5" />}
                aria-label="Add User"
                bg="none"
                _hover={{ bg: "none" }}
                onClick={inviteModal.onOpen}
              />
            </Tooltip>
            <Tooltip label="Share">
              <IconButton
                size="sm"
                icon={<FaShareAlt />}
                onClick={onShareOpen}
                aria-label="Share"
                bg="none"
                _hover={{ bg: "none" }}
              />
            </Tooltip>
            </>
            )}
          </>
        ) : (
         
          <>
           {selectedTrip && ( 
          <Menu>
            <MenuButton
              as={IconButton}
              icon={<BsThreeDotsVertical />}
              variant="ghost"
              size="sm"
              aria-label="More options"
            />
            <MenuList>
              {/* NEW — download option for mobile */}
              <MenuItem
                gap={2}
                onClick={handleDownloadPDF}
                isDisabled={downloading}
              >
                {<Box as={DownloadIcon} boxSize="5" />}
                <Translate text="Download" />
              </MenuItem>
              <MenuItem gap={2} onClick={inviteModal.onOpen}>
                <FaUserPlus />
                <Translate text="Add User" />
              </MenuItem>
              <MenuItem gap={2} onClick={onShareOpen}>
                <FaShareAlt />
                <Translate text="Share" />
              </MenuItem>
            </MenuList>
          </Menu>
           )}
          </>
         
        )}
        <SettingsMenu />
      </HStack>
      <InviteManagerModal
        tripId={selectedTrip}
        isOpen={inviteModal.isOpen}
        onClose={inviteModal.onClose}
        availableResource="budget_planner"
      />
    </HStack>
  );
};

export default BudgetHeader;
